import apiClient from './client';

// Paperform form type from API
export interface PaperformForm {
  id: string;
  title: string;
  slug: string;
  url: string;
  status: 'active' | 'draft' | 'archived';
  created_at: string;
  updated_at: string;
}

// Intake stats from API
export interface IntakeStats {
  total: number;
  sent: number;
  completed: number;
  pending: number;
  not_sent: number;
  completion_rate: number;
  intake_form_id: string | null;
  intake_configured_at: string | null;
  intake_due_date: string | null;
}

// Send intake request
export interface SendIntakeRequest {
  form_id: string;
  subject?: string;
  body?: string;
  participant_ids?: string[];
  due_date?: string;
}

// Send test email request
export interface SendTestEmailRequest {
  form_id: string;
  subject?: string;
  body?: string;
}

// Send test email result
export interface SendTestEmailResult {
  sent: boolean;
  email: string;
}

// Configure intake request
export interface ConfigureIntakeRequest {
  form_id: string;
}

// Send result
export interface SendIntakeResult {
  sent: number;
  failed: number;
  skipped?: number;
  errors: string[];
}

// Form submission from API
export interface FormSubmission {
  id: string;
  form_type: 'intake' | 'session_feedback' | 'mid_program' | 'final_survey' | 'nps' | 'other';
  paperform_form_id: string;
  paperform_submission_id: string;
  raw_payload: Array<{
    key: string;
    title: string;
    value: unknown;
    type: string;
  }>;
  submitted_at: string;
  created_at: string;
}

// Intake recipient for resend modal
export interface IntakeRecipient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  intake_status: 'completed' | 'pending' | 'not_configured';
  intake_form_sent_at: string | null;
  intake_completed_at: string | null;
  intake_reminder_count: number;
}

// MED-942: backfill historical responses summary
export interface BackfillMatched {
  paperform_submission_id: string;
  cohort_participant_id: string;
  participant_email: string;
}

export interface BackfillUnmatched {
  paperform_submission_id: string;
  participant_email: string;
}

export interface BackfillAmbiguous {
  paperform_submission_id: string;
  participant_email: string;
  match_count: number;
}

export interface BackfillNoEmail {
  paperform_submission_id: string;
}

export interface BackfillFailed {
  paperform_submission_id: string;
  error: string;
}

export interface BackfillHistoricalSummary {
  total_submissions_fetched: number;
  matched: BackfillMatched[];
  already_processed: number;
  unmatched: BackfillUnmatched[];
  ambiguous: BackfillAmbiguous[];
  no_email: BackfillNoEmail[];
  failed: BackfillFailed[];
  duration_ms: number;
}

// Forms list response
interface FormsListResponse {
  configured: boolean;
  forms: PaperformForm[];
  message?: string;
}

export const intakeApi = {
  /**
   * List available Paperform forms
   */
  async listForms(cohortId: string): Promise<FormsListResponse> {
    return apiClient.get<FormsListResponse>(`/api/cohorts/${cohortId}/intake/forms`);
  },

  /**
   * Get intake stats for a cohort
   */
  async getStats(cohortId: string): Promise<IntakeStats> {
    return apiClient.get<IntakeStats>(`/api/cohorts/${cohortId}/intake/stats`);
  },

  /**
   * Get all participants with intake status for the resend modal
   */
  async getRecipients(cohortId: string): Promise<IntakeRecipient[]> {
    return apiClient.get<IntakeRecipient[]>(`/api/cohorts/${cohortId}/intake/recipients`);
  },

  /**
   * Configure intake form without sending
   */
  async configureForm(cohortId: string, data: ConfigureIntakeRequest): Promise<void> {
    await apiClient.post(`/api/cohorts/${cohortId}/intake/configure`, data);
  },

  /**
   * Clear intake form configuration
   */
  async clearConfiguration(cohortId: string): Promise<void> {
    await apiClient.delete(`/api/cohorts/${cohortId}/intake/configure`);
  },

  /**
   * Send intake form emails
   */
  async sendIntakeEmails(cohortId: string, data: SendIntakeRequest): Promise<SendIntakeResult> {
    return apiClient.post<SendIntakeResult>(`/api/cohorts/${cohortId}/intake/send`, data);
  },

  /**
   * Send test intake email to logged-in user
   */
  async sendTestEmail(cohortId: string, data: SendTestEmailRequest): Promise<SendTestEmailResult> {
    return apiClient.post<SendTestEmailResult>(`/api/cohorts/${cohortId}/intake/test`, data);
  },

  /**
   * Send reminder emails
   */
  async sendReminders(
    cohortId: string,
    data: { subject?: string; body?: string; participant_ids?: string[] }
  ): Promise<SendIntakeResult> {
    return apiClient.post<SendIntakeResult>(`/api/cohorts/${cohortId}/intake/remind`, data);
  },

  /**
   * Get form submissions for a participant
   */
  async getParticipantSubmissions(
    cohortId: string,
    participantId: string
  ): Promise<FormSubmission[]> {
    return apiClient.get<FormSubmission[]>(
      `/api/cohorts/${cohortId}/intake/submissions/${participantId}`
    );
  },

  /**
   * MED-942: Pull historical Paperform responses for the cohort's matching form
   * and match them to participants by email. Idempotent — safe to re-run.
   */
  async pullHistoricalResponses(
    cohortId: string,
    formId: string
  ): Promise<BackfillHistoricalSummary> {
    return apiClient.post<BackfillHistoricalSummary>(
      `/api/cohorts/${cohortId}/intake/backfill`,
      { form_id: formId }
    );
  },
};
